import React, { useCallback } from 'react'
import { get, post, patch } from 'aws-amplify/api'

import { useAuth0 } from '@auth0/auth0-react'
import { buildPathWithQuery } from '../utils/buildQueryString'
import errorTrackingService from '../services/errorTrackingService'

import ProfileViewContext from '../context/ProfileViewContext'

const ProfileViewProvider = ({ children }) => {
  const { getIdTokenClaims } = useAuth0()

  const baseUrl = process.env.REACT_APP_API_BASE_URL
  const getProfileView = useCallback(async (tokenAuth) => {
    if (tokenAuth !== '') {
      try {
        const resp = get({
          apiName: baseUrl,
          path: '/api/v3/patients/profile',
          options: {
            headers: {
              'content-type': 'application/json',
              Authorization: `Bearer ${tokenAuth.__raw}`
            }
          }
        })
        const { body } = await resp.response
        const data = await body.json()

        if (data.success === false) {
          const error = new Error(data.message || 'API returned success: false')
          error.isAPIError = true
          error.response = { status: 200, data }
          errorTrackingService.trackAPIError(error, '/api/v3/patients/profile', 'GET')
          return undefined
        }

        return data
      } catch (error) {
        errorTrackingService.trackAPIError(error, '/api/v3/patients/profile', 'GET')
        console.log(error)
        return undefined
      }
    }
  }, [baseUrl])

  const resetPassword = async (tokenAuth) => {
    if (tokenAuth !== '') {
      try {
        const resp = get({
          apiName: baseUrl,
          path: '/auth/resetPassword',
          options: {
            headers: {
              'content-type': 'application/json',
              Authorization: `Bearer ${tokenAuth.__raw}`
            }
          }
        })
        const { body } = await resp.response
        const data = await body.json()

        if (data.success === false) {
          const error = new Error(data.message || 'API returned success: false')
          error.isAPIError = true
          error.response = { status: 200, data }
          errorTrackingService.trackAPIError(error, '/auth/resetPassword', 'GET')
          return undefined
        }

        return data
      } catch (error) {
        errorTrackingService.trackAPIError(error, '/auth/resetPassword', 'GET')
        console.log(error)
        return undefined
      }
    }
  }

  const getPatientPhoneNumber = useCallback(async (tokenAuth) => {
    if (tokenAuth !== '') {
      try {
        const resp = get({
          apiName: baseUrl,
          path: '/auth/getPatientPhoneNumber',
          options: {
            headers: {
              'content-type': 'application/json',
              Authorization: `Bearer ${tokenAuth.__raw}`
            }
          }
        })
        const { body } = await resp.response
        const data = await body.json()

        if (data.success === false) {
          const error = new Error(data.message || 'API returned success: false')
          error.isAPIError = true
          error.response = { status: 200, data }
          errorTrackingService.trackAPIError(error, '/auth/getPatientPhoneNumber', 'GET')
          return undefined
        }

        return data
      } catch (error) {
        errorTrackingService.trackAPIError(error, '/auth/getPatientPhoneNumber', 'GET')
        console.log(error)
        return undefined
      }
    }
  }, [baseUrl])

  const editProfile = useCallback(async (tokenAuth) => {
    if (tokenAuth !== '') {
      try {
        const resp = get({
          apiName: baseUrl,
          path: '/auth/editProfile',
          options: {
            headers: {
              'content-type': 'application/json',
              Authorization: `Bearer ${tokenAuth.__raw}`
            }
          }
        })
        const { body } = await resp.response
        const data = await body.json()

        if (data.success === false) {
          const error = new Error(data.message || 'API returned success: false')
          error.isAPIError = true
          error.response = { status: 200, data }
          errorTrackingService.trackAPIError(error, '/auth/editProfile', 'GET')
          return undefined
        }

        return data
      } catch (error) {
        errorTrackingService.trackAPIError(error, '/auth/editProfile', 'GET')
        console.log(error)
        return undefined
      }
    }
  }, [baseUrl])

  const getPatientLaboratories = useCallback(async (tokenAuth, page) => {
    if (tokenAuth !== '') {
      try {
        const pathWithParams = buildPathWithQuery('/health/getPatientLaboratories', {
          page,
          limit: 3
        })

        const resp = get({
          apiName: baseUrl,
          path: pathWithParams,
          options: {
            headers: {
              'content-type': 'application/json',
              Authorization: `Bearer ${tokenAuth.__raw}`
            }
          }
        })
        const { body } = await resp.response
        const data = await body.json()

        if (data.success === false) {
          const error = new Error(data.message || 'API returned success: false')
          error.isAPIError = true
          error.response = { status: 200, data }
          errorTrackingService.trackAPIError(error, '/health/getPatientLaboratories', 'GET')
          return undefined
        }

        return data.data
      } catch (error) {
        errorTrackingService.trackAPIError(error, '/health/getPatientLaboratories', 'GET')
        console.log(error)
        return undefined
      }
    }
  }, [baseUrl])

  const getPrescriptions = async (page) => {
    const idToken = await getIdTokenClaims()
    try {
      const pathWithParams = buildPathWithQuery('/health/prescriptions', {
        page,
        limit: 3
      })

      const resp = get({
        apiName: baseUrl,
        path: pathWithParams,
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${idToken.__raw}`
          }
        }
      })
      const { body } = await resp.response
      const data = await body.json()

      if (data.success === false) {
        const error = new Error(data.message || 'API returned success: false')
        error.isAPIError = true
        error.response = { status: 200, data }
        errorTrackingService.trackAPIError(error, '/health/prescriptions', 'GET')
        return undefined
      }

      return data.data
    } catch (error) {
      errorTrackingService.trackAPIError(error, '/health/prescriptions', 'GET')
      console.log(error)
      return undefined
    }
  }

  const getPatientInfo = useCallback(async (token) => {
    try {
      const resp = post({
        apiName: baseUrl,
        path: '/auth/getPatientInfo',
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${token.__raw}`
          },
          body: {}
        }
      })
      const { body } = await resp.response
      const data = await body.json()

      if (data.success === false) {
        const error = new Error(data.message || 'API returned success: false')
        error.isAPIError = true
        error.response = { status: 200, data }
        errorTrackingService.trackAPIError(error, '/auth/getPatientInfo', 'POST')
        return undefined
      }

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        userId: data.data._id || ''
      })

      return data
    } catch (error) {
      errorTrackingService.trackAPIError(error, '/auth/getPatientInfo', 'POST')
      console.log(error)
      return undefined
    }
  }, [baseUrl])

  const updatePatientInfo = useCallback(async (tokenAuth, body) => {
    if (tokenAuth !== '') {
      try {
        const resp = patch({
          apiName: baseUrl,
          path: '/auth/updatePatientInfo',
          options: {
            headers: {
              'content-type': 'application/json',
              Authorization: `Bearer ${tokenAuth.__raw}`
            },
            body
          }
        })
        const { body: responseBody } = await resp.response
        const data = await responseBody.json()

        if (data.success === false) {
          const error = new Error(data.message || 'API returned success: false')
          error.isAPIError = true
          error.response = { status: 200, data }
          errorTrackingService.trackAPIError(error, '/auth/updatePatientInfo', 'PATCH')
          return undefined
        }

        return data
      } catch (error) {
        errorTrackingService.trackAPIError(error, '/auth/updatePatientInfo', 'PATCH')
        console.log(error)
        return undefined
      }
    }
  }, [baseUrl])

  return (
    <ProfileViewContext.Provider
      value={{
        getProfileView,
        getPatientPhoneNumber,
        editProfile,
        getPatientLaboratories,
        getPrescriptions,
        updatePatientInfo,
        getPatientInfo,
        resetPassword
      }}
    >
      {children}
    </ProfileViewContext.Provider>
  )
}

export default ProfileViewProvider
